import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { getEmbedViewport, subscribeEmbedViewport } from "../../helper/embedViewport";

const EMBEDDED_MAX_HEIGHT = 860;
const EMBEDDED_MIN_HEIGHT = 600;
const VISIBLE_MIN_HEIGHT = 320;

// With the embed script the viewer covers exactly the visible part of the
// iframe and follows it while the page scrolls.
const visiblePlacement = (viewport) => ({
  embedded: true,
  visible: true,
  top: viewport.top,
  height: Math.max(viewport.height, VISIBLE_MIN_HEIGHT),
});

// An embedded iframe grows to the height of its page, so a fixed overlay
// would cover the whole page and the photo could end up off screen. There
// the viewer opens where the gallery is and scrolls into view.
const viewerPlacement = (anchor) => {
  const pageHeight = window.innerHeight;
  const embedded =
    window.parent !== window &&
    document.documentElement.scrollHeight <= pageHeight + 1;

  if (!embedded) {
    return { embedded: false, height: pageHeight };
  }

  const viewport = getEmbedViewport();
  if (viewport) {
    return visiblePlacement(viewport);
  }
  if (!anchor) {
    return { embedded: false, height: pageHeight };
  }

  const screenHeight = (window.screen?.availHeight || pageHeight) - 120;
  const height = Math.min(
    Math.max(screenHeight, EMBEDDED_MIN_HEIGHT),
    EMBEDDED_MAX_HEIGHT,
    pageHeight
  );
  const anchorTop = anchor.getBoundingClientRect().top + window.scrollY;

  return {
    embedded: true,
    height,
    top: Math.max(0, Math.min(anchorTop, pageHeight - height)),
  };
};

const PhotoViewer = ({ photos, startIndex = 0, title, anchorRef, onClose }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(startIndex);
  const [placement, setPlacement] = useState(() => viewerPlacement(anchorRef?.current));
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
      opener?.focus?.({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    if (!placement.visible) return undefined;

    return subscribeEmbedViewport((viewport) => setPlacement(visiblePlacement(viewport)));
  }, [placement.visible]);

  useLayoutEffect(() => {
    if (placement.embedded && !placement.visible) {
      panelRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    }
  }, [placement]);

  const items = photos.map((photo, position) => ({
    original: photo.full,
    thumbnail: photo.thumb,
    originalAlt: t("photo_of", {
      current: position + 1,
      total: photos.length,
      defaultValue: "Photo {{current}} of {{total}}",
    }),
  }));
  const several = photos.length > 1;

  return createPortal(
    <div
      className={`photo-viewer ${placement.embedded ? "photo-viewer--embedded" : ""}`}
      role='dialog'
      aria-modal='true'
      aria-label={title || t("show_all_photos", { count: photos.length, defaultValue: "Show all photos ({{count}})" })}
    >
      <div
        ref={panelRef}
        className='photo-viewer__panel'
        style={{
          "--photo-viewer-height": `${placement.height}px`,
          ...(placement.embedded ? { top: `${placement.top}px` } : {}),
        }}
      >
        <header className='photo-viewer__header'>
          <button
            ref={closeRef}
            type='button'
            className='photo-viewer__close'
            onClick={onClose}
          >
            <span aria-hidden='true'>×</span>
            {t("close", "Close")}
          </button>
          <span
            className='photo-viewer__counter'
            aria-live='polite'
            aria-label={t("photo_of", {
              current: index + 1,
              total: photos.length,
              defaultValue: "Photo {{current}} of {{total}}",
            })}
          >
            {index + 1} / {photos.length}
          </span>
        </header>
        <div className={`photo-viewer__gallery ${several ? "" : "photo-viewer__gallery--single"}`}>
          <ImageGallery
            items={items}
            startIndex={startIndex}
            lazyLoad
            showNav={several}
            showThumbnails={several}
            showBullets={false}
            showPlayButton={false}
            showFullscreenButton={false}
            onSlide={setIndex}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PhotoViewer;
